/* eslint-disable react-hooks/exhaustive-deps */
/*
  src/components/LexAcquireprofile.js
  — AI-agent marketplace: browse ▸ compare ▸ license / merge / hire.

  ✱ Firestore first.  If the market_agents collection is empty,
    we fall back to the in-file SAMPLE_AGENTS array (no fetch, no JSON file).
  ✱ ZERO breaking changes vs previous version.
*/

import React, { useState, useEffect, useMemo } from 'react';
import './lexacquire.css';
import { db } from '../firebase';
import { collection, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

/* ───────────────────────── sample data ───────────────────────── */
export const SAMPLE_AGENTS = [
  {
    id: 'fin-invoice-po',
    icon: '🧾',
    name: 'Invoice-PO Matcher',
    owner: 'Acme AI Labs',
    vertical: 'Finance',
    description:
      'Reconciles invoices to purchase orders with 98 % precision. Trained on 8 M documents.',
    tags: ['Accounting', 'OCR', 'Fraud'],
    mrr: 1200,
    growth: 18,
    churn: 2,
    price: 18000,
    last12moRevenue: 14400,
    modelCard: 'gpt-4o fine-tune · 2.6 B tokens',
    latency: 220,
    rating: 4.6,
    licenseOptions: [
      { type: 'per-seat', price: 49 },
      { type: 'annual enterprise', price: 9000 },
    ],
    saleOptions: {
      outrightPrice: 55000,
      revShareFloor: '12 % of future MRR',
    },
    mergeFit: ['cx-grpc-io', 'ops-route-opt'],
  },
  {
    id: 'sales-upsell',
    icon: '💹',
    name: 'Upsell Predictor',
    owner: 'SignalBoost',
    vertical: 'Sales',
    description:
      'Flags expansion-ready accounts; plug-in for HubSpot & Salesforce.',
    tags: ['Predictive', 'Churn', 'ML'],
    mrr: 980,
    growth: 32,
    churn: 3,
    price: 15000,
    last12moRevenue: 11760,
    latency: 110,
    rating: 4.7,
    licenseOptions: [{ type: 'performance (per closed-won)', price: 12 }],
    saleOptions: { outrightPrice: 42000 },
    mergeFit: ['sales-sdr-bot'],
  },
  {
    id: 'hr-screener',
    icon: '📝',
    name: 'Candidate Screener',
    owner: 'HireGen',
    vertical: 'HR',
    description:
      'Ranks CVs and surfaces top-5 % talent with bias guard-rails.',
    tags: ['Recruiting', 'DEI'],
    mrr: 1650,
    growth: 12,
    churn: 4,
    price: 22000,
    last12moRevenue: 19800,
    latency: 190,
    rating: 4.4,
    licenseOptions: [
      { type: 'SaaS monthly', price: 600 },
      { type: 'OEM SDK (annual)', price: 15000 },
    ],
    saleOptions: {
      outrightPrice: 65000,
      revShareFloor: '8 % for 24 mo',
    },
    mergeFit: ['hr-onboard-buddy', 'cs-triage'],
  },
  {
    id: 'it-incident-triage',
    icon: '🔥',
    name: 'Incident Triage Bot',
    owner: 'StackPulse',
    vertical: 'IT',
    description:
      'Summarises alerts & assigns owners; integrates PagerDuty / OpsGenie.',
    tags: ['DevOps', 'Observability'],
    mrr: 2100,
    growth: 25,
    churn: 3,
    price: 26000,
    last12moRevenue: 25200,
    latency: 95,
    rating: 4.8,
    licenseOptions: [{ type: 'per alert', price: 0.09 }],
    saleOptions: { outrightPrice: 88000 },
    mergeFit: ['it-cost-analyzer'],
  },
  {
    id: 'mkt-content-calendar',
    icon: '📅',
    name: 'Content Calendar Copilot',
    owner: 'CreativeFlow',
    vertical: 'Marketing',
    description:
      'Generates 30-day multi-channel calendar from brand voice & trends.',
    tags: ['Social', 'GenAI'],
    mrr: 730,
    growth: 40,
    churn: 5,
    price: 12000,
    last12moRevenue: 8760,
    latency: 140,
    rating: 4.5,
    licenseOptions: [
      { type: 'SaaS/mo', price: 49 },
      { type: 'white-label (annual)', price: 10000 },
    ],
    saleOptions: { outrightPrice: 30000 },
    mergeFit: ['mkt-seo-research'],
  },
  {
    id: 'ops-route-opt',
    icon: '🚚',
    name: 'Logistics Route Optimizer',
    owner: 'FleetIQ',
    vertical: 'Operations',
    description:
      'Minimises miles & fuel using real-time traffic + weather feeds.',
    tags: ['Routing', 'Optimization'],
    mrr: 2600,
    growth: 22,
    churn: 3,
    price: 28000,
    last12moRevenue: 31200,
    latency: 180,
    rating: 4.3,
    licenseOptions: [{ type: 'fleet (per truck/mo)', price: 14 }],
    saleOptions: { outrightPrice: 95000 },
    mergeFit: ['fin-invoice-po'],
  },
  {
    id: 'leg-nda-analyzer',
    icon: '🔍',
    name: 'NDA Analyzer',
    owner: 'ClauseGuard',
    vertical: 'Legal',
    description: 'Flags red-line clauses & suggests edits in 10 s.',
    tags: ['Contracts', 'Risk'],
    mrr: 860,
    growth: 28,
    churn: 4,
    price: 14000,
    last12moRevenue: 10320,
    latency: 130,
    rating: 4.9,
    licenseOptions: [
      { type: 'per doc', price: 4 },
      { type: 'firm (annual)', price: 18000 },
    ],
    saleOptions: { outrightPrice: 47000 },
    mergeFit: ['leg-policy-drafter'],
  },
  {
    id: 'cs-triage',
    icon: '🎫',
    name: 'Ticket Triage Assistant',
    owner: 'HelpHero',
    vertical: 'Support',
    description:
      'Routes tickets by topic & urgency – Zendesk, Intercom, Freshdesk.',
    tags: ['Customer-Support', 'NLP'],
    mrr: 1950,
    growth: 30,
    churn: 6,
    price: 24000,
    last12moRevenue: 23400,
    latency: 160,
    rating: 4.2,
    licenseOptions: [{ type: 'SaaS monthly', price: 499 }],
    saleOptions: { outrightPrice: 72000 },
    mergeFit: ['hr-screener'],
  },
  {
    id: 'anomaly-detector',
    icon: '🚨',
    name: 'Anomaly Detector',
    owner: 'DataSight',
    vertical: 'Analytics',
    description:
      'Spots KPI spikes & dips in real-time; Slack + Email alerts.',
    tags: ['Analytics', 'Time-Series'],
    mrr: 1420,
    growth: 35,
    churn: 3,
    price: 20000,
    last12moRevenue: 17040,
    latency: 90,
    rating: 4.6,
    licenseOptions: [{ type: 'SaaS/mo', price: 129 }],
    saleOptions: { outrightPrice: 61000 },
    mergeFit: ['an-dashboard-builder'],
  },
  {
    id: 'ux-access-checker',
    icon: '♿',
    name: 'Accessibility Checker',
    owner: 'InclusiveUX',
    vertical: 'UX',
    description: 'Finds WCAG issues & auto-fixes colour contrast in Figma.',
    tags: ['UX', 'Accessibility'],
    mrr: 540,
    growth: 44,
    churn: 4,
    price: 9000,
    last12moRevenue: 6480,
    latency: 150,
    rating: 4.1,
    licenseOptions: [
      { type: 'SaaS/mo', price: 29 },
      { type: 'Figma plugin (annual)', price: 6500 },
    ],
    saleOptions: { outrightPrice: 24000 },
    mergeFit: ['ux-figma-copy'],
  },
];

/* ────────────────────────── constants & helpers ────────────────────────── */

const AGENTS_COLLECTION = 'market_agents';
const DEALS_COLLECTION  = 'market_deals';

const TAGS = [
  'Sales','Marketing','HR','Ops','IT','Finance','Legal',
  'UX','Analytics','Support','Code','Design','Compliance',
];

const fuzzy = (q, str) => str.toLowerCase().includes(q.toLowerCase().trim());

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

const fadeSlide = {
  hidden:{opacity:0,y:12},visible:{opacity:1,y:0},exit:{opacity:0,y:12},
};

/* ───────────────────────────── component ──────────────────────────────── */

export default function LexAcquireprofile() {
  const [agents,setAgents]         = useState([]);
  const [loading,setLoading]       = useState(true);
  const [q,setQ]                   = useState('');
  const [tag,setTag]               = useState(null);
  const [priceCap,setPriceCap]     = useState(9999);
  const [sel,setSel]               = useState([]);
  const [show,setShow]             = useState(false);
  const [agent,setAgent]           = useState(null);
  const [tab,setTab]               = useState('license');
  const [pending,setPending]       = useState(false);

  /* first load – Firestore ➜ fallback to sample array */
  useEffect(()=>{
    (async()=>{
      try{
        const snap=await getDocs(collection(db,AGENTS_COLLECTION));
        setAgents(snap.size? snap.docs.map(d=>({id:d.id,...d.data()})): SAMPLE_AGENTS);
      }catch(e){
        console.error('market_agents fetch failed → using sample',e);
        setAgents(SAMPLE_AGENTS);
      }finally{ setLoading(false); }
    })();
  },[]);

  const list=useMemo(()=>agents
    .filter(a=>!tag||a.tags?.includes(tag))
    .filter(a=>a.price<=priceCap)
    .filter(a=>!q.trim()||fuzzy(q,a.name)||fuzzy(q,a.description))
  ,[agents,tag,priceCap,q]);

  const toggle=id=>setSel(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  const startDeal=async(kind)=>{
    if(!agent)return;
    setPending(true);
    try{
      await addDoc(collection(db,DEALS_COLLECTION),{
        agentId:agent.id,kind,buyer:'demoUser',
        createdAt:Timestamp.now(),status:'pending',
      });
      alert(`🎉 ${kind.toUpperCase()} deal sent (stub)`);
      setShow(false);setSel([]);
    }catch(e){console.error(e);alert('deal error')}
    finally{setPending(false);}
  };

  /* ─────────────────────────── render ─────────────────────────── */

  return(
    <div className="lexacq-container">
      <header className="lexacq-header">
        <h1>🪙 Lex Acquire — AI-Agent Marketplace</h1>
        <div className="lexacq-search">
          <input placeholder="Search agents…" value={q} onChange={e=>setQ(e.target.value)}/>
          <input type="number" min="0" className="lexacq-price" title="Max monthly $" value={priceCap}
                 onChange={e=>setPriceCap(+e.target.value)}/>
        </div>
      </header>

      <div className="lexacq-tags">
        {TAGS.map(t=>(
          <button key={t} className={`tag-chip ${tag===t?'active':''}`}
                  onClick={()=>setTag(tag===t?null:t)}>{t}</button>
        ))}
      </div>

      {loading? <p className="lexacq-note">Loading agents…</p>:
        list.length===0? <p className="lexacq-note">No agents match your filters.</p>:
        <div className="lexacq-grid">
          {list.map(a=>(
            <motion.div key={a.id} variants={fadeSlide} initial="hidden" animate="visible" exit="exit"
              className={`agent-card ${sel.includes(a.id)?'selected':''}`} onClick={()=>toggle(a.id)}>
              <div className="agent-head">
                <h2>{a.icon??'🤖'} {a.name}</h2>
                <span className="agent-price">{fmt.format(a.price)}/mo</span>
              </div>
              <p className="agent-desc">{a.description}</p>
              <div className="agent-kpis">
                <span>📈 {a.growth}%</span>
                <span>💰 {fmt.format(a.mrr)}</span>
                <span>🏷 {a.tags?.[0]??'—'}</span>
              </div>
              <input type="checkbox" className="agent-check" checked={sel.includes(a.id)}
                     onChange={()=>toggle(a.id)} title="select"/>
            </motion.div>
          ))}
        </div>}

      <AnimatePresence>
        {sel.length>1&&(
          <motion.div className="compare-drawer" initial={{y:80,opacity:0}}
                      animate={{y:0,opacity:1}} exit={{y:80,opacity:0}}>
            <strong>{sel.length}</strong>&nbsp;selected –&nbsp;
            <button onClick={()=>setSel([])}>Clear</button>
            <button className="primary"
              onClick={()=>{
                const first=agents.find(a=>a.id===sel[0]);
                setAgent(first);setShow(true);
              }}>
              Compare / Acquire →
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {show&&agent&&(
          <motion.div className="lexacq-modal-overlay" initial={{opacity:0}}
                      animate={{opacity:1}} exit={{opacity:0}}
                      onClick={()=>setShow(false)}>
            <motion.div className="lexacq-modal" initial={{scale:.92,opacity:0}}
                        animate={{scale:1,opacity:1}} exit={{scale:.92,opacity:0}}
                        onClick={e=>e.stopPropagation()}>
              <h2>{agent.icon??'🤖'} {agent.name}</h2>

              <div className="lexacq-tabs">
                {['license','merge','hire'].map(t=>(
                  <button key={t} className={tab===t?'active':''} onClick={()=>setTab(t)}>
                    {t==='license'&&'🛒 License'}
                    {t==='merge'  &&'🤝 Merge'}
                    {t==='hire'   &&'⏱ Hire'}
                  </button>
                ))}
              </div>

              {tab==='license'&&(
                <div className="tab-pane">
                  <p>One-click perpetual license (12-month updates).<br/>
                     Price <strong>{fmt.format(agent.price)}</strong></p>
                  <button disabled={pending} onClick={()=>startDeal('license')}
                          className="primary wide">
                    {pending?'Processing…':'Buy & Deploy →'}
                  </button>
                </div>
              )}

              {tab==='merge'&&(
                <div className="tab-pane">
                  <p>Propose a capability merge – revenue-share, weight-share.</p>
                  <pre className="diff-block">{agent.diffPreview??'// TODO diff'}</pre>
                  <button disabled={pending} onClick={()=>startDeal('merge')}
                          className="primary wide violet">
                    {pending?'Processing…':'Propose Merge →'}
                  </button>
                </div>
              )}

              {tab==='hire'&&(
                <div className="tab-pane">
                  <p>Hire on-demand (token-capped).</p>
                  <label>Tokens budget
                    <input type="number" min="1000" defaultValue="10000"/>
                  </label>
                  <button disabled={pending} onClick={()=>startDeal('hire')}
                          className="primary wide green">
                    {pending?'Processing…':'Start Contract →'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <button className="smartbot-btn"
              onClick={()=>alert('🤖 Coming soon: autonomous procurement assistant!')}
              title="Find me targets">✚</button>
    </div>
  );
}
